import { useEffect, useState } from 'react';
import {
  ALL_CAMPUS_FACILITIES,
  matchesFacilityFilter
} from 'components/Campus/DestinationFacilityFilter';
import { useCampusDestinations } from 'hooks/useCampusDestinations';
import { facilityLabelById, formatCampusLocation } from 'utils/campusLabels';
import {
  DestinationFacility,
  DestinationFacilityZone,
  DestinationKind,
  destinationFacilityId,
  SpotStatus,
  spotStatus
} from 'utils/destinationAvailability';
import { zoneColor } from 'utils/zoneStyle';

export interface ModalYardSpot {
  id: string;
  number: number;
  displayNumber: string;
  zoneId: string;
  facility_zone_uuid: string;
  status: SpotStatus;
  driverId: null;
  driverName: null;
  trailerId: null;
  trailerNumber: null;
  shipmentNumber: null;
  facilityId?: string;
  isLocked?: boolean;
}

export interface ModalZone {
  id: string;
  name: string;
  color: string;
  zone_type?: string;
}

export interface ModalDockDoor {
  door: string;
  dockUuid: string;
  zoneUuid: string;
  zoneName: string;
  hasMoveFromTask?: boolean;
  trailerUuid?: string;
  configuration?: any;
  facilityId?: string;
}

interface Fallback<S, Z, FZ, D> {
  yardSpots?: S[];
  zones?: Z[];
  facilityZones?: FZ[];
  dockDoors?: D[];
  lockedSpots?: Set<string>;
}

interface Options<S, Z, FZ, D> {
  /** Facility the trailer is at; the campus around it is offered. */
  sourceFacilityId: string | number | null | undefined;
  /** Load (and keep the facility filter) only while the modal is open. */
  enabled: boolean;
  /** The page's single-facility data, used when the campus has one facility. */
  fallback: Fallback<S, Z, FZ, D>;
}

/**
 * Destination lists for a move modal: the whole campus (filtered by the facility
 * dropdown, labeled with the facility) in a multi-facility campus, else `fallback`.
 */
export function useModalDestinations<
  S = ModalYardSpot,
  Z = ModalZone,
  FZ = DestinationFacilityZone,
  D = ModalDockDoor
>({ sourceFacilityId, enabled, fallback }: Options<S, Z, FZ, D>) {
  const destinations = useCampusDestinations(sourceFacilityId, enabled);
  const campusMode = destinations.isMultiFacility;
  const [facilityFilter, setFacilityFilter] = useState<string>(ALL_CAMPUS_FACILITIES);

  // Every modal opens on "All campus facilities".
  useEffect(() => {
    if (!enabled) setFacilityFilter(ALL_CAMPUS_FACILITIES);
  }, [enabled]);

  const inFilter = (item: { facilityId?: string }) =>
    matchesFacilityFilter(item, facilityFilter);
  const facilityLabel = (facilityId?: string) =>
    facilityLabelById(destinations.facilities, facilityId);
  const label = (name: string, facilityId?: string) =>
    formatCampusLocation(name, facilityLabel(facilityId), true);

  const campusYardSpots: ModalYardSpot[] = destinations.yardSpots
    .filter(inFilter)
    .map((spot) => ({
      id: spot.yard_spot_uuid,
      number: parseInt(spot.yard_spot_name.replace(/\D/g, ''), 10) || 0,
      displayNumber: spot.yard_spot_name,
      zoneId: spot.facility_zone_uuid,
      facility_zone_uuid: spot.facility_zone_uuid,
      status: spotStatus(spot),
      driverId: null,
      driverName: null,
      trailerId: null,
      trailerNumber: null,
      shipmentNumber: null,
      facilityId: spot.facilityId,
      isLocked: spot.isLocked
    }));

  const campusZones: ModalZone[] = destinations.facilityZones
    .filter(inFilter)
    .map((zone) => ({
      id: zone.facility_zone_uuid,
      name: label(zone.facility_zone_name, zone.facilityId),
      color: zoneColor(zone),
      zone_type: zone.zone_type
    }));

  const campusFacilityZones: DestinationFacilityZone[] = destinations.facilityZones
    .filter(inFilter)
    .map((zone) => ({
      ...zone,
      facility_zone_name: label(zone.facility_zone_name, zone.facilityId)
    }));

  const campusDockDoors: ModalDockDoor[] = destinations.dockDoors
    .filter((door) => !door.isLocked && inFilter(door))
    .map((door) => ({
      door: door.dockName,
      dockUuid: door.dockUuid,
      zoneUuid: door.zoneUuid,
      zoneName: label(door.zoneName, door.facilityId),
      hasMoveFromTask: door.hasMoveFromTask,
      trailerUuid: door.trailer_uuid || undefined,
      configuration: door.configuration,
      facilityId: door.facilityId
    }));

  const lockedSpots: Set<string> = campusMode
    ? new Set(
        destinations.yardSpots
          .filter((spot) => spot.isLocked)
          .map((spot) => spot.yard_spot_uuid)
      )
    : fallback.lockedSpots || new Set();

  return {
    campusMode,
    loading: destinations.loading,
    facilities: destinations.facilities as DestinationFacility[],
    dockFacilities: destinations.facilities.filter((f) => f.canDock),
    /** The raw campus destinations, unfiltered. */
    destinations,
    facilityFilter,
    setFacilityFilter,
    /** Label cards with their facility: campus mode with no facility picked. */
    showFacility: campusMode && facilityFilter === ALL_CAMPUS_FACILITIES,
    inFilter,
    yardSpots: (campusMode ? campusYardSpots : fallback.yardSpots || []) as Array<
      S | ModalYardSpot
    >,
    zones: (campusMode ? campusZones : fallback.zones || []) as Array<Z | ModalZone>,
    facilityZones: (campusMode
      ? campusFacilityZones
      : fallback.facilityZones || []) as Array<FZ | DestinationFacilityZone>,
    dockDoors: (campusMode ? campusDockDoors : fallback.dockDoors || []) as Array<
      D | ModalDockDoor
    >,
    lockedSpots,
    isSpotLocked: (spotId: string) => lockedSpots.has(spotId),
    /** Facility of a chosen destination; undefined outside campus mode (same-facility move). */
    facilityOf: (kind: DestinationKind, uuid: string | null | undefined) =>
      campusMode ? destinationFacilityId(destinations, kind, uuid) : undefined,
    facilityLabel
  };
}
